import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Save, X, ChevronDown, Pencil, Plus,
         Phone as PhoneIcon, Voicemail, Mail } from "lucide-react";

// Batch-Dialer LEAD INFO tab. Two columns:
//   • Left  — form fields (First/Last/Address/City/State/Postal +
//             Mailing× + Contact List) matching the Batch mockup.
//   • Right — phone list (up to 5 numbers) + email row with edit +
//             plus-add pattern. Blank rows render as an underline
//             strip like Batch's screenshot.
//
// CANCEL reverts local state · SAVE PATCHes /api/leads/{id}. Renders
// only when a lead is active. Uses inline underline inputs (border-b
// only) for the Batch aesthetic.

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY","DC",
];

function splitName(fullName) {
  const parts = String(fullName || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first: "", last: "" };
  if (parts.length === 1) return { first: parts[0], last: "" };
  return { first: parts[0], last: parts.slice(1).join(" ") };
}

function splitAddress(raw) {
  const s = String(raw || "").trim();
  if (!s) return { street: "", city: "", state: "", postal: "" };
  const parts = s.split(",").map((p) => p.trim());
  const street = parts[0] || "";
  const city   = parts[1] || "";
  const tail   = parts[2] || "";
  const m = tail.match(/([A-Z]{2})\s*(\d{5}(?:-\d{4})?)?/i);
  return {
    street, city,
    state: (m && m[1]) ? m[1].toUpperCase() : "",
    postal: (m && m[2]) ? m[2] : "",
  };
}

function joinAddress({ street, city, state, postal }) {
  const bits = [street].filter(Boolean);
  const csp = [city, [state, postal].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  if (csp) bits.push(csp);
  return bits.join(", ");
}

function seed(lead) {
  const nm = splitName(lead?.name);
  const addr = splitAddress(lead?.property_address);
  const mail = splitAddress(lead?.mailing_address || "");
  return {
    first_name:      lead?.first_name || nm.first || "",
    last_name:       lead?.last_name  || nm.last  || "",
    address:         addr.street,
    city:            lead?.property_city  || addr.city  || "",
    state:           lead?.property_state || addr.state || "",
    postal_code:     lead?.property_zip   || addr.postal || "",
    mailing_address: mail.street,
    mailing_city:    lead?.mailing_city   || mail.city  || "",
    mailing_state:   lead?.mailing_state  || mail.state || "",
    mailing_postal:  lead?.mailing_zip    || mail.postal || "",
    list_name:       lead?.list_name || lead?.source_tag || "",
    phone_1:         lead?.phone || "",
    phone_2:         lead?.phone_2 || "",
    phone_3:         lead?.phone_3 || "",
    phone_4:         lead?.phone_4 || "",
    phone_5:         lead?.phone_5 || "",
    email:           lead?.email || "",
  };
}

export default function DialerLeadInfoPanel({ lead, onSaved, slotUnderEmail }) {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setForm(lead ? seed(lead) : null);
  }, [lead?.id]);   // eslint-disable-line react-hooks/exhaustive-deps

  if (!lead || !form) return null;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        first_name: form.first_name,
        last_name:  form.last_name,
        name: `${form.first_name} ${form.last_name}`.trim() || lead.name,
        property_address: joinAddress({
          street: form.address, city: form.city, state: form.state, postal: form.postal_code,
        }),
        property_city:  form.city,
        property_state: form.state,
        property_zip:   form.postal_code,
        mailing_address: joinAddress({
          street: form.mailing_address, city: form.mailing_city,
          state: form.mailing_state, postal: form.mailing_postal,
        }),
        mailing_city:  form.mailing_city,
        mailing_state: form.mailing_state,
        mailing_zip:   form.mailing_postal,
        list_name:     form.list_name,
        phone:   form.phone_1,
        phone_2: form.phone_2,
        phone_3: form.phone_3,
        phone_4: form.phone_4,
        phone_5: form.phone_5,
        email:   form.email,
      };
      const { data } = await api.patch(`/leads/${lead.id}`, payload);
      toast.success("Lead saved");
      onSaved && onSaved(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const cancel = () => {
    setForm(seed(lead));
    toast.info("Reverted unsaved changes");
  };

  const phoneRows = ["phone_1","phone_2","phone_3","phone_4","phone_5"];

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm"
         data-testid="dialer-lead-info-panel">
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] gap-x-8 gap-y-0 p-5">
        {/* ═════ LEFT — form fields ═════ */}
        <div className="space-y-3.5" data-testid="dlif-form-col">
          <TwoCol>
            <BatchField label="First name" value={form.first_name} onChange={set("first_name")}
                        testid="dlif-first-name" />
            <BatchField label="Last name"  value={form.last_name}  onChange={set("last_name")}
                        testid="dlif-last-name" />
          </TwoCol>
          <BatchField label="Address" value={form.address} onChange={set("address")}
                      testid="dlif-address" />
          <BatchField label="City" value={form.city} onChange={set("city")}
                      testid="dlif-city" />
          <TwoCol>
            <BatchSelect label="State" value={form.state} onChange={set("state")}
                         options={US_STATES} testid="dlif-state" />
            <BatchField label="Postal Code" value={form.postal_code} onChange={set("postal_code")}
                        testid="dlif-postal" />
          </TwoCol>
          <BatchField label="Mailing Address" value={form.mailing_address} onChange={set("mailing_address")}
                      testid="dlif-mailing-address" />
          <BatchField label="Mailing City" value={form.mailing_city} onChange={set("mailing_city")}
                      testid="dlif-mailing-city" />
          <TwoCol>
            <BatchSelect label="Mailing State" value={form.mailing_state} onChange={set("mailing_state")}
                         options={US_STATES} testid="dlif-mailing-state" />
            <BatchField label="Mailing Postal Code" value={form.mailing_postal} onChange={set("mailing_postal")}
                        testid="dlif-mailing-postal" />
          </TwoCol>

          <div className="pt-2">
            <Label className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
              Contact List
            </Label>
            <div className="mt-1 flex items-center rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm">
              <span className="flex-1 truncate text-slate-700" data-testid="dlif-list-name">
                {form.list_name || <span className="italic text-slate-400">No list assigned</span>}
              </span>
              <button className="ml-2 text-slate-400 hover:text-slate-600" data-testid="dlif-list-clear"
                      onClick={() => setForm({ ...form, list_name: "" })}>
                <X size={12} />
              </button>
              <ChevronDown size={14} className="text-slate-400 ml-1" />
            </div>
          </div>
        </div>

        {/* ═════ RIGHT — phones + email list ═════ */}
        <div className="space-y-1 mt-6 lg:mt-0" data-testid="dlif-phones-col">
          {phoneRows.map((key, idx) => (
            <PhoneRow
              key={key}
              icon={idx === 0 ? <PhoneIcon size={13} className="text-emerald-600" />
                    : idx === 1 ? <PhoneIcon size={13} className="text-slate-400" />
                    : idx === 2 ? <PhoneIcon size={13} className="text-slate-400" />
                    : idx === 3 ? <PhoneIcon size={13} className="text-emerald-600" />
                    :             <Voicemail size={13} className="text-slate-400" />}
              value={form[key]}
              onChange={set(key)}
              testid={`dlif-${key}`}
            />
          ))}
          <div className="flex justify-end pt-1">
            <button className="w-6 h-6 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white flex items-center justify-center transition"
                    data-testid="dlif-add-phone" title="Add phone (uses next free slot)"
                    onClick={() => {
                      const k = phoneRows.find((p) => !form[p]);
                      if (k) toast.info(`Type into ${k.replace("_"," ")} to add`);
                      else toast.error("All 5 phone slots used");
                    }}>
              <Plus size={13} />
            </button>
          </div>
          <PhoneRow
            icon={<Mail size={13} className="text-slate-400" />}
            value={form.email}
            onChange={set("email")}
            testid="dlif-email-row"
            placeholder="email@example.com"
          />
          {/* Owner ask 2026-08-28 (QuickOfferButtons_37): 3-tier offer
              ladder now renders directly under the email row inside the
              phones column so reps have the full pitch context in one
              vertical column. */}
          {slotUnderEmail}
        </div>
      </div>

      {/* ═════ FOOTER — Cancel · Save · timestamps ═════ */}
      <div className="border-t border-slate-200 px-5 py-3 flex items-center justify-center gap-3">
        <Button variant="outline" onClick={cancel} disabled={busy}
                className="rounded-md h-9 px-6 border-slate-300 text-slate-500 hover:bg-slate-50 uppercase text-xs tracking-widest"
                data-testid="dlif-cancel-btn">
          <X size={13} className="mr-1.5" /> Cancel
        </Button>
        <Button onClick={save} disabled={busy}
                className="rounded-md h-9 px-6 bg-slate-200 text-slate-600 hover:bg-slate-300 uppercase text-xs tracking-widest"
                data-testid="dlif-save-btn">
          {busy ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Save size={13} className="mr-1.5" />}
          Save
        </Button>
      </div>
      <div className="px-5 pb-3 text-[10px] text-slate-400 text-right" data-testid="dlif-created-line">
        Created: {lead.created_at ? new Date(lead.created_at).toLocaleString() : "—"}
        {lead.updated_at ? <> · Modified: {new Date(lead.updated_at).toLocaleString()}</> : null}
      </div>
    </div>
  );
}

// ————————————— tiny building blocks —————————————

function TwoCol({ children }) {
  return <div className="grid grid-cols-2 gap-4">{children}</div>;
}

function BatchField({ label, value, onChange, testid, placeholder }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
        {label}
      </Label>
      <Input value={value || ""} onChange={onChange} placeholder={placeholder}
             className="mt-0.5 h-8 border-0 border-b border-slate-200 rounded-none focus-visible:ring-0 focus-visible:border-fuchsia-500 px-0 text-sm bg-transparent"
             data-testid={testid} />
    </div>
  );
}

function BatchSelect({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
        {label}
      </Label>
      <div className="relative mt-0.5">
        <select value={value || ""} onChange={onChange}
                className="w-full h-8 border-0 border-b border-slate-200 rounded-none focus-visible:ring-0 focus:border-fuchsia-500 bg-transparent text-sm appearance-none pr-6"
                data-testid={testid}>
          <option value=""></option>
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <ChevronDown size={12} className="absolute right-1 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
      </div>
    </div>
  );
}

function PhoneRow({ icon, value, onChange, testid, placeholder = "(555) 000-0000" }) {
  return (
    <div className="flex items-center gap-2 py-1.5 border-b border-slate-100 last:border-b-0">
      <span className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
        {icon}
      </span>
      <input value={value || ""} onChange={onChange} placeholder={placeholder}
             className="flex-1 h-7 border-0 focus:outline-none text-sm bg-transparent font-mono text-slate-700 min-w-0"
             data-testid={testid} />
      <button className="text-slate-300 hover:text-slate-500 shrink-0"
              data-testid={`${testid}-edit`} title="Edit (tap the field)">
        <Pencil size={11} />
      </button>
    </div>
  );
}
